import { useState } from 'react';
import { FileMdIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';

import type { SkillFile } from '@/api-models/skills';
import { getFileIcon } from '@/features/agent-sessions/components/tabs/files/file-icons';
import { Button, IconButton } from '@/ui/button/Button';
import { Input } from '@/ui/Input';

/** The directories a skill bundle is expected to use. Anything else is grouped under "Other". */
const KNOWN_GROUPS = ['scripts', 'references', 'assets'] as const;

/**
 * The file list for one skill.
 *
 * Deliberately **not** the session `FileTree`: that one is bound to `sandbox-files.listDirectory`,
 * lazy directory expansion, and the selection store. A skill holds tens of files, not thousands,
 * so a flat list grouped by top-level directory shows the whole bundle at once with no expansion
 * state to manage.
 */
export function SkillFileTree({
  files,
  selected,
  readOnly,
  onSelect,
  onCreate,
  onDelete,
}: {
  files: SkillFile[];
  selected: string;
  readOnly: boolean;
  onSelect: (path: string) => void;
  onCreate: (path: string) => Promise<void>;
  onDelete: (path: string) => void;
}) {
  const [adding, setAdding] = useState(false);

  // SKILL.md first — it is the skill — then the known folders in a fixed order, then the rest.
  const root = files.filter((f) => !f.path.includes('/') && f.path !== 'SKILL.md');
  const grouped = KNOWN_GROUPS.map((dir) => ({
    dir,
    files: files.filter((f) => f.path.startsWith(`${dir}/`)),
  })).filter((g) => g.files.length > 0);
  const other = files.filter((f) => f.path.includes('/') && !KNOWN_GROUPS.some((dir) => f.path.startsWith(`${dir}/`)));

  return (
    <div className="flex w-60 shrink-0 flex-col gap-1 overflow-y-auto rounded-lg border border-line bg-surface-2 p-2">
      <FileRow path="SKILL.md" label="SKILL.md" selected={selected === 'SKILL.md'} onSelect={onSelect} />

      {root.map((file) => (
        <FileRow
          key={file.path}
          path={file.path}
          label={file.path}
          selected={selected === file.path}
          readOnly={readOnly}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      ))}

      {[...grouped, ...(other.length ? [{ dir: 'Other', files: other }] : [])].map((group) => (
        <div key={group.dir} className="mt-2 flex flex-col gap-0.5">
          <span className="px-2 text-2xs font-medium uppercase tracking-wide text-fg-tertiary">{group.dir}</span>
          {group.files.map((file) => (
            <FileRow
              key={file.path}
              path={file.path}
              label={file.path.slice(file.path.indexOf('/') + 1)}
              selected={selected === file.path}
              readOnly={readOnly}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          ))}
        </div>
      ))}

      {!readOnly &&
        (adding ? (
          <NewFileRow
            existing={files.map((f) => f.path)}
            onCancel={() => setAdding(false)}
            onCreate={async (path) => {
              await onCreate(path);
              setAdding(false);
              onSelect(path);
            }}
          />
        ) : (
          <div className="mt-2 flex">
            <Button
              variant="ghost"
              size={7}
              onClick={() => setAdding(true)}
              iconLeft={<PlusIcon size={14} weight="bold" />}
            >
              New file
            </Button>
          </div>
        ))}
    </div>
  );
}

function FileRow({
  path,
  label,
  selected,
  readOnly,
  onSelect,
  onDelete,
}: {
  path: string;
  label: string;
  selected: boolean;
  readOnly?: boolean;
  onSelect: (path: string) => void;
  onDelete?: (path: string) => void;
}) {
  // SKILL.md is undeletable — a skill without it cannot be loaded, which the API enforces too.
  const deletable = !readOnly && onDelete && path !== 'SKILL.md';
  const Icon = path === 'SKILL.md' ? FileMdIcon : getFileIcon(path);

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-md px-2 py-1 ${
        selected ? 'bg-surface-4 text-fg' : 'text-fg-secondary hover:bg-surface-3'
      }`}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        onClick={() => onSelect(path)}
      >
        <Icon size={14} className="shrink-0" />
        <span className="truncate text-xs">{label}</span>
      </button>
      {deletable && (
        <IconButton
          size={7}
          aria-label={`Delete ${path}`}
          className="opacity-0 group-hover:opacity-100"
          onClick={() => onDelete(path)}
        >
          <TrashIcon size={13} />
        </IconButton>
      )}
    </div>
  );
}

function NewFileRow({
  existing,
  onCancel,
  onCreate,
}: {
  existing: string[];
  onCancel: () => void;
  onCreate: (path: string) => Promise<void>;
}) {
  const [path, setPath] = useState('scripts/');
  const [busy, setBusy] = useState(false);

  const trimmed = path.trim();
  // Mirrors `normalize_skill_path`; the server is still the authority, this just avoids a
  // round trip to be told something obvious.
  const error = !trimmed
    ? null
    : existing.includes(trimmed)
      ? 'That file already exists.'
      : trimmed === 'SKILL.md'
        ? 'SKILL.md already exists.'
        : !/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/.test(trimmed) || trimmed.includes('..')
          ? 'Letters, digits, dots, dashes and underscores, separated by "/".'
          : trimmed.split('/').some((part) => part.startsWith('.'))
            ? 'Path segments must not start with a dot.'
            : null;

  async function submit() {
    if (!trimmed || error || busy) return;
    setBusy(true);
    try {
      await onCreate(trimmed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 flex flex-col gap-1">
      <Input
        value={path}
        autoFocus
        placeholder="scripts/run.sh"
        onChange={(e) => setPath(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
          if (e.key === 'Escape') onCancel();
        }}
      />
      {error && <p className="px-1 text-2xs text-danger">{error}</p>}
      <div className="flex gap-1">
        <Button variant="primary" size={7} disabled={!trimmed || Boolean(error) || busy} onClick={submit}>
          Add
        </Button>
        <Button variant="ghost" size={7} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
